import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { getSnappers } from "../services/snapperService";
import "./SnapperList.css";
import "./SnapperListAadhaar.css";

const SnapperList = () => {
  const [snappers, setSnappers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination states (Limit set to 50)
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter states
  const [search, setSearch] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [status, setStatus] = useState("");
  const [activeJob, setActiveJob] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getSnappers({
        page,
        limit,
        search,
        city,
        status,
        activeJob,
      });

      console.log("========== SNAPPER DEBUG ==========");
      console.log("API RESPONSE:", data);
      console.log("ITEMS:", data?.items);
      console.log("ITEM COUNT:", data?.items?.length);
      console.log("TOTAL:", data?.total);
      console.log("TOTAL PAGES:", data?.total_pages);
      console.log("===================================");

      setSnappers(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total) || 0);
      setTotalPages(Number(data?.total_pages) || 0);
    } catch (err) {
      console.error("Error loading snappers:", err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, city, status, activeJob]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    return () => {
      if (hoverCloseTimer.current) clearTimeout(hoverCloseTimer.current);
    };
  }, []);

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  const resetFilters = () => {
    setSearch("");
    setCity("");
    setState("");
    setStatus("");
    setActiveJob("");
    setPage(1);
  };

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "") return "—";
    return `${Number(amount).toLocaleString("en-IN")} INR`;
  };

  const getAadhaarStatusLabel = (status) => {
    const labels = {
      verified: "Verified",
      link_sent: "Link Sent",
      link_created: "Link Created",
      otp_sent: "OTP Sent",
      failed: "Failed",
      not_started: "Not Started",
    };
    return labels[String(status || "not_started").toLowerCase()] || status || "Not Started";
  };

  const getAadhaarStatusClass = (status) => {
    const classes = {
      verified: "aadhaar-status--verified",
      link_sent: "aadhaar-status--sent",
      link_created: "aadhaar-status--pending",
      otp_sent: "aadhaar-status--pending",
      failed: "aadhaar-status--failed",
      not_started: "aadhaar-status--not-started",
    };
    return classes[String(status || "not_started").toLowerCase()] || "aadhaar-status--not-started";
  };

  // Hover card is rendered in document.body so the table's horizontal
  // scrolling wrapper cannot clip the Aadhaar details.
  const [aadhaarHover, setAadhaarHover] = useState(null);
  const [bankHover, setBankHover] = useState(null);
  const hoverCloseTimer = useRef(null);

  const clearHoverCloseTimer = () => {
    if (hoverCloseTimer.current) {
      clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
  };

  const openAadhaarHover = (snapper, event) => {
    clearHoverCloseTimer();
    const rect = event.currentTarget.getBoundingClientRect();
    const cardWidth = 320;
    const left = Math.max(
      12,
      Math.min(rect.left, window.innerWidth - cardWidth - 12)
    );
    const estimatedCardHeight = 220;
    const top =
      window.innerHeight - rect.bottom < estimatedCardHeight
        ? Math.max(12, rect.top - estimatedCardHeight - 8)
        : rect.bottom + 8;

    setAadhaarHover({ snapper, top, left });
  };

  const scheduleAadhaarHoverClose = () => {
    clearHoverCloseTimer();
    hoverCloseTimer.current = setTimeout(() => {
      setAadhaarHover(null);
      setBankHover(null);
      hoverCloseTimer.current = null;
    }, 160);
  };

  const openBankHover = (snapper, event) => {
    clearHoverCloseTimer();
    const rect = event.currentTarget.getBoundingClientRect();
    const cardWidth = 340;
    const left = Math.max(
      12,
      Math.min(rect.left, window.innerWidth - cardWidth - 12)
    );
    const estimatedCardHeight = 230;
    const top =
      window.innerHeight - rect.bottom < estimatedCardHeight
        ? Math.max(12, rect.top - estimatedCardHeight - 8)
        : rect.bottom + 8;

    setAadhaarHover(null);
    setBankHover({ snapper, top, left });
  };

  const getGenderLabel = (value) => {
    if (value === 1 || String(value).trim() === "1") return "Male";
    if (value === 0 || String(value).trim() === "0") return "Female";

    const normalized = String(value || "").trim().toLowerCase();
    if (["male", "m"].includes(normalized)) return "Male";
    if (["female", "f"].includes(normalized)) return "Female";
    return value || "—";
  };

  // A false comparison means a confirmed mismatch. null/undefined stays neutral.
  const getIdentityFieldClass = (isMatch) =>
    isMatch === false
      ? "aadhaar-hover-field aadhaar-hover-field--mismatch"
      : "aadhaar-hover-field";

  const renderAadhaarCell = (snapper) => {
    const aadhaarStatus = String(snapper.aadhaar_status || "not_started").toLowerCase();
    const details = snapper.aadhaar_details;
    const isVerified = aadhaarStatus === "verified";
    const bankAccountExists =
      snapper.bank_account_exists === true ||
      snapper.bank_account_exists === 1 ||
      snapper.bank_account_exists === "1";
    const bankAccountMissing = !bankAccountExists;

    return (
      <div className="aadhaar-cell">
        <div className="aadhaar-status-row">
          {isVerified ? (
            <button
              type="button"
              className="aadhaar-status aadhaar-status--verified aadhaar-verified-trigger"
              onMouseEnter={(event) => openAadhaarHover(snapper, event)}
              onMouseLeave={scheduleAadhaarHoverClose}
              onFocus={(event) => openAadhaarHover(snapper, event)}
              onBlur={scheduleAadhaarHoverClose}
              aria-label="Verified Aadhaar. Hover or focus to view Aadhaar details."
            >
              <svg
                className="aadhaar-card-icon"
                viewBox="0 0 20 16"
                aria-hidden="true"
                focusable="false"
              >
                <rect x="1" y="1" width="18" height="14" rx="2" />
                <circle cx="6" cy="6" r="2" />
                <path d="M3.5 12c.5-1.8 1.4-2.6 2.5-2.6s2 .8 2.5 2.6M11 5h5M11 8h5M11 11h3" />
              </svg>
              Verified
            </button>
          ) : (
            <span className={`aadhaar-status ${getAadhaarStatusClass(aadhaarStatus)}`}>
              {getAadhaarStatusLabel(aadhaarStatus)}
            </span>
          )}

          {bankAccountExists && (
            <button
              type="button"
              className="bank-account-icon-wrap bank-account-icon-wrap--available"
              aria-label="View bank account details"
              onMouseEnter={(event) => openBankHover(snapper, event)}
              onMouseLeave={scheduleAadhaarHoverClose}
              onFocus={(event) => openBankHover(snapper, event)}
              onBlur={scheduleAadhaarHoverClose}
            >
              <svg className="bank-account-icon bank-account-icon--available"
                   viewBox="0 0 24 24" aria-hidden="true">
                <path d="M3 9h18L12 4 3 9Z" />
                <path d="M5 10v8m4-8v8m6-8v8m4-8v8M3 20h18M2 22h20" />
              </svg>
            </button>
          )}

          {!bankAccountExists && isVerified && (
            <span
              className="bank-account-icon-wrap bank-account-icon-wrap--missing"
              role="img"
              aria-label="Bank account not added"
              onMouseEnter={clearHoverCloseTimer}
            >
              <img
                className="bank-account-icon bank-account-icon--missing"
                src="/assets/bank-account-icon-red.png"
                alt=""
                width="25"
                height="25"
              />
              <span className="bank-account-tooltip" role="tooltip">
                Bank Account Not Added
              </span>
            </span>
          )}
        </div>

        {isVerified && aadhaarHover?.snapper?.id === snapper.id && details &&
          createPortal(
            <div
              className="aadhaar-hover-card"
              style={{ top: `${aadhaarHover.top}px`, left: `${aadhaarHover.left}px` }}
              onMouseEnter={clearHoverCloseTimer}
              onMouseLeave={scheduleAadhaarHoverClose}
              role="tooltip"
            >
              <div className="aadhaar-hover-card__header">
                <span className="aadhaar-hover-card__title">Aadhaar Details</span>
                <span className="aadhaar-hover-card__verified">Verified</span>
              </div>
              <div className={getIdentityFieldClass(snapper.name_match)}>
                <strong>Name:</strong> {details.name || "—"}
              </div>
              <div className="aadhaar-hover-field-row">
                <div className={getIdentityFieldClass(snapper.gender_match)}>
                  <strong>Gender:</strong> {details.gender || "—"}
                </div>
                <div className={getIdentityFieldClass(snapper.dob_match)}>
                  <strong>DOB:</strong> {details.dob || "—"}
                </div>
              </div>
              <div className="aadhaar-hover-field aadhaar-hover-field--address">
                <strong>Address:</strong> {details.address || "—"}
              </div>
            </div>,
            document.body
          )}

        {bankAccountExists && bankHover?.snapper?.id === snapper.id &&
          createPortal(
            <div
              className="aadhaar-hover-card bank-hover-card"
              style={{ top: `${bankHover.top}px`, left: `${bankHover.left}px` }}
              onMouseEnter={clearHoverCloseTimer}
              onMouseLeave={scheduleAadhaarHoverClose}
              role="tooltip"
            >
              <div className="aadhaar-hover-card__header">
                <span className="aadhaar-hover-card__title">Bank Account Details</span>
                <span className="bank-hover-card__badge">Added</span>
              </div>
              <div className={getIdentityFieldClass(snapper.name_match)}>
                <strong>Name:</strong> {snapper.bank_account_name || "—"}
              </div>
              <div className="aadhaar-hover-field-row">
                <div className={getIdentityFieldClass(snapper.gender_match)}>
                  <strong>Gender:</strong> {getGenderLabel(snapper.bank_account_gender)}
                </div>
                <div className={getIdentityFieldClass(snapper.dob_match)}>
                  <strong>DOB:</strong> {snapper.bank_account_dob || "—"}
                </div>
              </div>
              <div className="aadhaar-hover-field">
                <strong>Bank Name:</strong> {snapper.bank_name || "—"}
              </div>

              <div className="aadhaar-hover-field">
                <strong>Account Number:</strong> {snapper.account_number || "—"}
              </div>

              <div className="aadhaar-hover-field">
                <strong>IFSC Code:</strong> {snapper.ifsc_code || "—"}
              </div>
            </div>,
            document.body
          )}
      </div>
    );
  };

  const startRecord = (page - 1) * limit + 1;
  const endRecord = Math.min(page * limit, total);

  return (
    <div className="snapper-list-container">
      {/* Page Header */}
      <div className="snapper-list-header">
        <div className="header-title-group">
          <h1>Snapper Directory</h1>
          <p>Manage and audit field photographer coverage and engagement</p>
        </div>
        <div className="header-actions">
          <div className="total-badge">
            Total Records: <strong>{total.toLocaleString()}</strong>
          </div>
          <button className="action-btn-refresh" onClick={fetchData}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Filter Toolbar Shell */}
      <div className="snapper-filter-card">
        <div className="filter-label">
          <span className="filter-icon">⚱</span>
          <span>Filter By</span>
        </div>

        <div className="filter-controls">
          <input
            type="text"
            className="filter-input"
            placeholder="Search name, phone, email, ID..."
            value={search}
            onChange={handleFilterChange(setSearch)}
          />

          <input
            type="text"
            className="filter-input"
            placeholder="City..."
            value={city}
            onChange={handleFilterChange(setCity)}
          />

          <input
            type="text"
            className="filter-input"
            placeholder="State..."
            value={state}
            onChange={handleFilterChange(setState)}
          />

          <select
            className="filter-select"
            value={status}
            onChange={handleFilterChange(setStatus)}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            className="filter-select"
            value={activeJob}
            onChange={handleFilterChange(setActiveJob)}
          >
            <option value="">Has Active Job</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>

          <button className="btn-reset-filter" onClick={resetFilters}>
            Reset Filter
          </button>
        </div>
      </div>

      {/* Main Table Shell */}
      <div className="snapper-table-card">
        <div className="table-status-bar">
          Showing <b>{total > 0 ? `${startRecord}–${endRecord}` : 0}</b> of{" "}
          <b>{total.toLocaleString()}</b> snappers
        </div>

        <div className="table-scroll-wrapper">
          <table className="snapper-data-table">
            <thead>
              <tr>
                <th>SNAPPER NAME</th>
                <th>SNAPPER ID</th>
                <th>JOBS<br />(LIFETIME)</th>
                <th>JOBS (30D)</th>
                <th>EARNED<br />(LIFETIME)</th>
                <th>EARNED (30D)</th>
                <th>CITY</th>
                <th>STATE</th>
                <th>STATUS</th>
                <th>LAST LOGIN</th>
                <th>ACTIVE JOB</th>
                <th>ID VERIFIED</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="table-message-state">
                    Loading snapper records...
                  </td>
                </tr>
              ) : snappers.length === 0 ? (
                <tr>
                  <td colSpan="12" className="table-message-state">
                    No snappers match the selected filters.
                  </td>
                </tr>
              ) : (
                snappers.map((snapper) => (
                  <tr key={snapper.id}>
                    <td>
                      <div className="cell-snapper-profile">
                        <strong className="profile-name">
                          {snapper.name || "—"}
                        </strong>
                        {snapper.email && (
                          <span className="profile-email">{snapper.email}</span>
                        )}
                      </div>
                    </td>
                    <td className="cell-mono-id">#{snapper.id}</td>
                    <td>{snapper.jobs_lifetime ?? 0}</td>
                    <td>{snapper.jobs_30d ?? 0}</td>
                    <td>{formatCurrency(snapper.earned_lifetime)}</td>
                    <td>{formatCurrency(snapper.earned_30d)}</td>
                    <td>{snapper.city || "—"}</td>
                    <td>{snapper.state || "—"}</td>
                    <td>
                      <span
                        className={`status-pill status-pill--${
                          String(snapper.status).toLowerCase() === "active"
                            ? "active"
                            : "inactive"
                        }`}
                      >
                        {snapper.status || "—"}
                      </span>
                    </td>
                    <td>{formatDate(snapper.last_login_at)}</td>
                    <td>
                      <span
                        className={`badge-indicator ${
                          snapper.has_active_job ? "indicator-yes" : "indicator-no"
                        }`}
                      >
                        {snapper.has_active_job ? "Y" : "N"}
                      </span>
                    </td>
                    <td className="aadhaar-table-cell">
                      {renderAadhaarCell(snapper)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Original Pagination Controls */}
        <div className="pagination">
          <button
            disabled={page === 1 || loading}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
          >
            Previous
          </button>

          <span>
            Page <strong>{page}</strong> of <strong>{totalPages || 1}</strong>
          </span>

          <button
            disabled={page >= totalPages || loading}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default SnapperList;